import { FunctionComponent, useState } from "react";
import { LucideIcon } from "lucide-react";

import { useAppDispatch } from "../../../features/helpers";
import { openToast } from "../../../store/ToastSlice";
import { formatCurrencyBRL } from "../../../utils/format";

import { Modal, FormField, Alert } from "../../ui";

export interface ProfitActionModalProps {
    open: boolean;
    onClose: () => void;

    icon: LucideIcon;
    title: string;
    subtitle?: string;

    actionLabel: string;
    actionIcon?: LucideIcon;

    maxValue: number;

    onConfirm: (value: number) => Promise<boolean>;

    successTitle: string;
    successMessage: string;
    errorTitle?: string;
    errorMessage?: string;
}

const ProfitActionModal: FunctionComponent<ProfitActionModalProps> = ({
    open,
    onClose,
    icon,
    title,
    subtitle,
    actionLabel,
    actionIcon: ActionIcon,
    maxValue,
    onConfirm,
    successTitle,
    successMessage,
    errorTitle = "Nao foi possivel concluir",
    errorMessage = "Tente novamente em instantes.",
}) => {

    const dispatch = useAppDispatch();

    const [cents, setCents]     = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError]     = useState<string>("");

    const valueReais = cents / 100;
    const display    = formatCurrencyBRL(valueReais);
    const exceeds    = valueReais > maxValue;
    const canSubmit  = valueReais > 0 && !exceeds && !loading;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, "");
        setCents(Number(raw.slice(0, 15)));
    };

    const reset = () => {
        setCents(0);
        setError("");
    };

    const handleClose = () => {
        if (loading) return;
        reset();
        onClose();
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;

        setLoading(true);
        setError("");

        try {
            const ok = await onConfirm(valueReais);
            if (!ok) throw new Error(errorMessage);

            dispatch(openToast({
                title:   successTitle,
                message: `${successMessage} (${display})`,
                effect:  "success",
            }));
            reset();
            onClose();
        } catch (err: any) {
            const msg = err?.message || errorMessage;
            setError(msg);
            dispatch(openToast({
                title:   errorTitle,
                message: msg,
                effect:  "error",
            }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            icon={icon}
            title={title}
            subtitle={subtitle}
            maxWidth={420}
            footer={
                <button
                    className="btn-pill primary"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {loading
                        ? <div className="loader" />
                        : <>{ActionIcon && <ActionIcon size={16} />} {actionLabel}</>}
                </button>
            }
        >
            {error && <Alert variant="error">{error}</Alert>}

            <div className="avaiable" style={{ marginBottom: 16 }}>
                <span>saldo disponivel</span>
                <p>
                    <span className="num">{formatCurrencyBRL(maxValue)}</span>
                </p>
            </div>

            <FormField
                label="Valor"
                htmlFor="profit-action-value"
                error={exceeds ? "Valor excede o saldo da carteira de lucro." : undefined}
                hint={!exceeds ? "Digite o valor em reais" : undefined}
            >
                <input
                    type="text"
                    inputMode="numeric"
                    id="profit-action-value"
                    placeholder="R$ 0,00"
                    value={display}
                    onChange={handleChange}
                    disabled={loading}
                />
            </FormField>
        </Modal>
    );
};

export default ProfitActionModal;
